import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { QUERY_CACHE_TIME, QUERY_STALE_TIME } from "@shared/config/constants";
import { isRetryOnStatus } from "@shared/helpers";
import { ApiError } from "@shared/api";
import { useHandleResponse, type QueryMeta } from "@shared/api/hooks";
import type { MutationMeta } from "@shared/api/hooks/mutation";
import { setActiveQueryClient } from "./active-query-client";

// Create query client outside to avoid recreating on each render
let queryClientInstance: QueryClient | null = null;

const getQueryClient = (
  handleError: ReturnType<typeof useHandleResponse>["handleError"],
  handleSuccess: ReturnType<typeof useHandleResponse>["handleSuccess"]
) => {
  if (!queryClientInstance) {
    queryClientInstance = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          gcTime: QUERY_CACHE_TIME,
          staleTime: QUERY_STALE_TIME.MEDIUM,
          retry(failureCount, error: unknown) {
            const RetryCount = 3;
            const status = ApiError.isApiError(error) ? error.statusCode : 0;
            return failureCount < RetryCount && isRetryOnStatus(status);
          },
          throwOnError(error, query) {
            console.error("Query error:", error, query);
            // Global error handling - triggered unless passThrough is false
            const meta = query.meta as QueryMeta | undefined;
            if (meta?.passThrough !== false) {
              handleError({
                error,
                context: meta?.errorContext,
                customMessage: meta?.customErrorMessage,
                navigateOnError: meta?.navigateOnError ?? true,
                showToast: !meta?.withoutToastMessage,
              });
            }
            return false;
          },
        },
        mutations: {
          onError(error: unknown, _, context) {
            console.error("Mutation error:", error);
            // Global error handling - triggered unless passThrough is false
            const meta = (context as { meta?: MutationMeta })?.meta;
            if (meta?.passThrough !== false) {
              handleError({
                error,
                context: meta?.errorContext,
                customMessage: meta?.customErrorMessage,
                navigateOnError: meta?.navigateOnError ?? true,
                showToast: !meta?.withoutToastMessage,
              });
            }
          },
          onSuccess(data, _, context) {
            // Global success handling - triggered unless passThrough is false
            const meta = (context as { meta?: MutationMeta })?.meta;
            if (
              meta?.passThrough !== false &&
              !meta?.withoutToastMessage &&
              (meta?.showSuccessToast || meta?.customSuccessMessage)
            ) {
              handleSuccess({
                data,
                context: meta?.successContext,
                customMessage: meta?.customSuccessMessage,
              });
            }
          },
        },
      },
    });
  }
  setActiveQueryClient(queryClientInstance);
  return queryClientInstance;
};

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const { handleError, handleSuccess } = useHandleResponse();
  const queryClient = getQueryClient(handleError, handleSuccess);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
